/**
 * ItemSearchInput — Debounced search input for Item / Item Group selection
 *
 * Uses frappe.client.get_list with like filter for search.
 * Displays results in a dropdown below the input.
 *
 * Part of Plan 199, Epic 02.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { frappeMethod } from "@/lib/erp/utils";
import { X } from "lucide-react";

interface Props {
  doctype: "Item" | "Item Group";
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function ItemSearchInput({
  doctype,
  value,
  onChange,
  placeholder,
}: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const search = useCallback(
    async (term: string) => {
      if (!term || term.length < 1) {
        setResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const data = await frappeMethod<{ name: string }[]>(
          "frappe.client.get_list",
          {
            doctype,
            filters: [["name", "like", `%${term}%`]],
            fields: ["name"],
            limit_page_length: 20,
          }
        );
        setResults((data || []).map((d) => d.name));
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [doctype]
  );

  const handleInputChange = useCallback(
    (val: string) => {
      setQuery(val);
      setIsOpen(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => search(val), 300);
    },
    [search]
  );

  const handleSelect = useCallback(
    (item: string) => {
      onChange(item);
      setQuery("");
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange("");
    setQuery("");
  }, [onChange]);

  // If value is set, show it as a chip
  if (value) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-background">
        <span className="text-sm flex-1 truncate">{value}</span>
        <button
          type="button"
          onClick={handleClear}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => query && setIsOpen(true)}
        placeholder={placeholder}
        className="h-10"
      />

      {isOpen && (results.length > 0 || isSearching) && (
        <div className="absolute z-20 top-full mt-1 w-full bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {isSearching && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Searching...
            </div>
          )}
          {results.map((item) => (
            <button
              key={item}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
              onClick={() => handleSelect(item)}
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
